import { createConsumer } from '@rails/actioncable';

const PRESENCE_INTERVAL = 20000;
const RECONNECT_INTERVAL = 1000;
const FORCE_REOPEN_AFTER = 10000;
const REOPEN_THROTTLE = 10000;
const RELOAD_AFTER = 45000;
const RELOAD_GRACE_PERIOD = 5000;
const RELOAD_COOLDOWN = 300000;
const RELOAD_GUARD_KEY = 'chatwoot_action_cable_recovery_reload_at';

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(
    app,
    pubsubToken,
    websocketHost = '',
    presenceInterval = PRESENCE_INTERVAL
  ) {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        disconnected: () => {
          BaseActionCableConnector.isDisconnected = true;
          this.disconnectedAt ??= Date.now();
          this.onDisconnected();
          this.initReconnectTimer();
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.disconnectedAt = null;
    this.lastReopenAt = null;
    this.isAValidEvent = () => true;
    this.triggerPresenceInterval = () => {
      setTimeout(() => {
        this.subscription.updatePresence();
        this.triggerPresenceInterval();
      }, presenceInterval);
    };
    this.triggerPresenceInterval();
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
      this.resetRecoveryState();
      return;
    }

    if (navigator.onLine) {
      this.recoverConnection();
    }
    this.initReconnectTimer();
  }

  recoverConnection = () => {
    const now = Date.now();
    const disconnectedFor = now - this.disconnectedAt;
    const timeSinceLastReopen = now - this.lastReopenAt;

    const shouldReload =
      disconnectedFor >= RELOAD_AFTER &&
      this.lastReopenAt &&
      timeSinceLastReopen >= RELOAD_GRACE_PERIOD;

    if (shouldReload && this.markReloadAttempt(now)) {
      window.location.reload();
      return;
    }

    const shouldForceReopen =
      disconnectedFor >= FORCE_REOPEN_AFTER &&
      (!this.lastReopenAt || timeSinceLastReopen >= REOPEN_THROTTLE);

    if (shouldForceReopen) {
      this.consumer.connection.reopen();
      this.lastReopenAt = now;
    }
  };

  // eslint-disable-next-line class-methods-use-this
  markReloadAttempt = now => {
    try {
      const lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY));
      if (lastReloadAt && now - lastReloadAt < RELOAD_COOLDOWN) {
        return false;
      }
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
      return true;
    } catch {
      return false;
    }
  };

  resetRecoveryState = () => {
    this.disconnectedAt = null;
    this.lastReopenAt = null;
  };

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  disconnect() {
    this.clearReconnectTimer();
    this.resetRecoveryState();
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
